import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2, CheckCircle2, AlertTriangle, XCircle, MinusCircle, ChevronRight, Sparkles, Database, Filter, FileText, Lightbulb } from "lucide-react";
import api from "../lib/api";
import { StatusBadge } from "./AIOperations";

const STEP_ICON = {
  Successful: <CheckCircle2 className="h-4 w-4 text-emerald-500" />,
  Warning: <AlertTriangle className="h-4 w-4 text-amber-500" />,
  Failed: <XCircle className="h-4 w-4 text-red-500" />,
  Skipped: <MinusCircle className="h-4 w-4 text-muted-foreground" />,
};
const SRC_STYLE = { Healthy: "text-emerald-500", Partial: "text-amber-500", Failed: "text-red-500", Unavailable: "text-muted-foreground" };
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
const fmtDur = (ms) => (ms == null ? "—" : ms < 1000 ? `${ms}ms` : ms < 60000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms / 60000)}m`);

const Section = ({ icon, title, children, testid }) => (
  <div className="rounded-xl border border-border bg-card p-5" data-testid={testid}>
    <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2 mb-4">{icon} {title}</h2>
    {children}
  </div>
);

export default function AIRunDetail() {
  const { runId } = useParams();
  const nav = useNavigate();
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/ai-ops/runs/${runId}`).then((res) => setR(res.data)).finally(() => setLoading(false));
  }, [runId]);

  if (loading) return <div className="flex items-center gap-2 text-muted-foreground p-10"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>;
  if (!r) return <div className="p-10 text-muted-foreground">Run not found.</div>;

  const a = r.angela_analysis || {};
  const nr = r.no_results_diag;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1100px] mx-auto space-y-5" data-testid="ai-run-detail">
      <button onClick={() => nav("/ai-operations")} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="run-back"><ArrowLeft className="h-4 w-4" /> AI Operations</button>

      {/* Header */}
      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-full bg-primary/15 text-primary flex items-center justify-center text-base font-bold">{r.agent_avatar || "A"}</div>
            <div>
              <div className="text-lg font-semibold">{r.task}</div>
              <div className="text-sm text-muted-foreground">{r.agent_name} · {r.agent_role}</div>
            </div>
          </div>
          <StatusBadge status={r.status} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 mt-4 text-xs">
          <Meta label="Automation" value={r.automation_name || r.buybox_name || "—"} />
          <Meta label="Trigger" value={r.trigger} />
          <Meta label="Module" value={r.module} />
          <Meta label="Run ID" value={(r.id || "").slice(0, 8)} />
          <Meta label="Started" value={fmtTime(r.started_at)} />
          <Meta label="Completed" value={fmtTime(r.completed_at)} />
          <Meta label="Duration" value={fmtDur(r.duration_ms)} />
          {r.apn && <Meta label="APN" value={r.apn} />}
        </div>
      </div>

      {/* No-results diagnostics */}
      {nr && (
        <Section icon={<AlertTriangle className="h-4 w-4" />} title="No-Results Analysis" testid="no-results-analysis">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3 text-sm">
            <Meta label="Search Completed" value={nr.search_completed ? "Yes" : "No"} />
            <Meta label="Sources Attempted" value={nr.sources_attempted} />
            <Meta label="Sources Successful" value={nr.sources_successful} />
            <Meta label="Properties Found" value={nr.properties_found} />
            <Meta label="Properties Qualified" value={nr.properties_qualified} />
          </div>
          <div className="text-sm rounded-md bg-secondary/40 border border-border px-3 py-2">{nr.case_label}</div>
        </Section>
      )}

      {/* Angela analysis */}
      <Section icon={<Sparkles className="h-4 w-4" />} title="Angela Run Analysis" testid="angela-analysis">
        <p className="text-sm leading-relaxed">{a.summary}</p>
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <List title="What Worked" items={a.what_worked} />
          <List title="Why Results Were Limited" items={a.why_limited} />
          <List title="Primary Constraints" items={a.primary_constraints} />
          <List title="Source Failures" items={a.source_failures} />
        </div>
      </Section>

      {/* Recommendations */}
      {(a.recommendations || []).length > 0 && (
        <Section icon={<Lightbulb className="h-4 w-4" />} title="Angela's Recommendations" testid="recommendations">
          <div className="space-y-2">
            {a.recommendations.map((rec, i) => (
              <div key={i} className="rounded-md border border-border bg-secondary/30 p-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`recommendation-${i}`}>
                <div>
                  <div className="font-medium text-sm">{rec.title}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Estimated impact: {rec.impact}</div>
                </div>
                <span className="text-[10px] text-muted-foreground italic">Review manually — Angela never changes your Buy Box automatically</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Funnel */}
      {(r.funnel || []).length > 0 && (
        <Section icon={<Filter className="h-4 w-4" />} title="Search Funnel" testid="search-funnel">
          <div className="space-y-1.5">
            {r.funnel.map((f, i) => (
              <div key={i} data-testid={`funnel-step-${i}`}>
                <div className="flex items-center justify-between text-sm">
                  <span>{f.label}</span>
                  <span className="font-semibold">{f.remaining}{f.eliminated > 0 && <span className="text-red-500 text-xs font-normal ml-2">−{f.eliminated} ({f.pct}%)</span>}</span>
                </div>
                <div className="h-2 rounded-full bg-secondary mt-1 overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: `${r.funnel[0].remaining ? Math.round(f.remaining / r.funnel[0].remaining * 100) : 0}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Rejections */}
      {(r.rejections || []).length > 0 && (
        <Section icon={<XCircle className="h-4 w-4" />} title="Rejection / Exclusion Breakdown" testid="rejections">
          <div className="space-y-1.5">
            {r.rejections.map((rej, i) => (
              <div key={i} className="flex items-center justify-between text-sm border-b border-border/40 pb-1.5">
                <span>{rej.reason}</span><span className="font-semibold text-red-500">{rej.count}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Inputs */}
      {r.inputs && Object.keys(r.inputs).length > 0 && (
        <Section icon={<FileText className="h-4 w-4" />} title="Inputs Used" testid="inputs-used">
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
            {Object.entries(r.inputs).map(([k, v]) => (
              <div key={k} className="flex gap-2"><span className="text-muted-foreground shrink-0">{k}:</span><span className="font-medium">{String(v)}</span></div>
            ))}
          </div>
        </Section>
      )}

      {/* Timeline */}
      {(r.timeline || []).length > 0 && (
        <Section icon={<ChevronRight className="h-4 w-4" />} title="Processing Timeline" testid="timeline">
          <ol className="space-y-2">
            {r.timeline.map((s, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm">
                <span className="mt-0.5">{STEP_ICON[s.status] || STEP_ICON.Skipped}</span>
                <div><div>{s.label}</div>{s.detail && <div className="text-xs text-muted-foreground">{s.detail}</div>}</div>
              </li>
            ))}
          </ol>
        </Section>
      )}

      {/* Source Health */}
      {(r.sources || []).length > 0 && (
        <Section icon={<Database className="h-4 w-4" />} title="Source Health" testid="source-health">
          <div className="space-y-2">
            {r.sources.map((s, i) => (
              <div key={i} className="rounded-md border border-border p-3" data-testid={`source-${i}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-medium text-sm">{s.name}</span>
                  <span className={`text-xs font-semibold ${SRC_STYLE[s.status] || "text-muted-foreground"}`}>{s.status}</span>
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-1 mt-1.5 text-xs text-muted-foreground">
                  {s.records_returned != null && <span>Returned: {s.records_returned}</span>}
                  {s.unique != null && <span>Unique: {s.unique}</span>}
                  {s.qualified != null && <span>Qualified: {s.qualified}</span>}
                  {s.response_ms != null && <span>Response: {fmtDur(s.response_ms)}</span>}
                </div>
                {s.error && <div className="text-xs text-red-500 mt-1.5">Error: {s.error}</div>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Records created / updated */}
      {((r.records_created || []).length > 0 || (r.records_updated || []).length > 0) && (
        <Section icon={<FileText className="h-4 w-4" />} title="Records Created or Updated" testid="records-touched">
          {(r.records_created || []).length > 0 && (
            <div className="mb-3">
              <div className="text-xs font-semibold text-emerald-500 mb-1">Created ({r.records_created.length})</div>
              {r.records_created.map((rec, i) => (
                <Link key={i} to={rec.link} className="block text-sm text-primary hover:underline" data-testid={`created-record-${i}`}>{rec.type} — {rec.label}</Link>
              ))}
            </div>
          )}
          {(r.records_updated || []).length > 0 && (
            <div>
              <div className="text-xs font-semibold text-sky-400 mb-1">Updated ({r.records_updated.length})</div>
              {r.records_updated.slice(0, 30).map((rec, i) => (
                <Link key={i} to={rec.link} className="block text-sm text-primary hover:underline">{rec.type} — {rec.label}</Link>
              ))}
            </div>
          )}
        </Section>
      )}
    </div>
  );
}

const Meta = ({ label, value }) => (
  <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div><div className="font-medium truncate">{value ?? "—"}</div></div>
);
const List = ({ title, items }) => {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">{title}</div>
      <ul className="list-disc list-inside text-sm space-y-0.5">{items.map((x, i) => <li key={i}>{x}</li>)}</ul>
    </div>
  );
};
